import {
  ARROW,
  type ChangelogEntryDto,
  type ChangelogSnapshot,
  formatLogTimestamp,
  formatSlotRange,
  isAssignmentSnapshot,
  isPersonSnapshot,
  paletteColorName,
} from '@betreuung/shared';
import { PersonChip } from '@/components/shared/PersonChip';
import { de } from '@/i18n/de';
import type { PersonLike, PersonLookup } from '@/lib/persons';

export interface ChangeEntryProps {
  entry: ChangelogEntryDto;
  persons: PersonLookup;
  /** Reference point for the "Heute"/"Gestern" timestamps. */
  now: Date;
}

/**
 * One protocol entry: who changed what, when, and the before/after pair.
 *
 * The affected person, colour and period all come from the entry's own
 * snapshots rather than from the current person list, so an entry stays truthful
 * after a rename, a recolouring or a deletion.
 */
export function ChangeEntry({ entry, persons, now }: ChangeEntryProps) {
  const assignment = isAssignmentSnapshot(entry.after)
    ? entry.after
    : isAssignmentSnapshot(entry.before)
      ? entry.before
      : null;

  return (
    <div className="border-b border-divider px-4 py-3">
      <div className="flex items-center gap-2">
        <ActorChip entry={entry} persons={persons} />
        <span className="min-w-0 flex-1 text-label font-medium text-fg-secondary">
          {actionLabel(entry, persons)}
        </span>
        <span className="shrink-0 font-mono text-meta font-medium text-fg-hint">
          {formatLogTimestamp(entry.ts, now)}
        </span>
      </div>

      {assignment ? (
        <p className="pt-1.5 font-mono text-hint font-medium text-fg-tertiary">
          {formatSlotRange(assignment.startSlot, assignment.endSlot, { withYear: true })}
        </p>
      ) : null}

      {entry.entity === 'assignment' ? (
        <div className="flex items-center gap-2 pt-1.5">
          <PersonChip person={assignmentPerson(entry.before)} />
          <span className="text-label text-fg-hint">{ARROW}</span>
          <PersonChip person={assignmentPerson(entry.after)} />
        </div>
      ) : null}

      {personChanges(entry).map((change) => (
        <p key={change} className="pt-1.5 text-label text-fg-tertiary">
          {change}
        </p>
      ))}
    </div>
  );
}

/**
 * The system writes entries without an actor, and an actor whose person was
 * deleted has no colour left to show; both use the neutral chip.
 */
function ActorChip({ entry, persons }: { entry: ChangelogEntryDto; persons: PersonLookup }) {
  const colorIndex =
    entry.actorPersonId === null ? undefined : persons.get(entry.actorPersonId)?.colorIndex;
  const name = entry.actorName ?? de.changes.systemActor;

  if (colorIndex === undefined) {
    return (
      <span className="inline-flex shrink-0 items-center rounded-badge bg-fg-secondary px-2 py-0.5 text-chip font-bold text-surface">
        {name}
      </span>
    );
  }
  return <PersonChip person={{ name, colorIndex }} size="sm" />;
}

function actionLabel(entry: ChangelogEntryDto, persons: PersonLookup): string {
  if (entry.entity === 'assignment') {
    if (entry.action === 'created') return de.changes.assignmentCreated;
    if (entry.action === 'deleted') return de.changes.assignmentDeleted;
    return de.changes.assignmentUpdated;
  }

  const snapshot = isPersonSnapshot(entry.after)
    ? entry.after
    : isPersonSnapshot(entry.before)
      ? entry.before
      : null;
  const name = snapshot?.name ?? persons.describe(entry.entityId).name;

  if (entry.action === 'created') return de.changes.personCreated(name);
  if (entry.action === 'deleted') return de.changes.personDeleted(name);
  return de.changes.personUpdated(name);
}

/** `null` renders the dashed chip: the free side of a creation or deletion. */
function assignmentPerson(snapshot: ChangelogSnapshot | null): PersonLike | null {
  return isAssignmentSnapshot(snapshot)
    ? { name: snapshot.personName, colorIndex: snapshot.colorIndex }
    : null;
}

/**
 * Person entries cover no period, so instead of a chip pair they list the
 * fields that actually differ. A password reset is deliberately not derivable
 * from the snapshots and therefore not listed.
 */
function personChanges(entry: ChangelogEntryDto): string[] {
  if (entry.entity !== 'person' || entry.action !== 'updated') return [];

  const { before, after } = entry;
  if (!isPersonSnapshot(before) || !isPersonSnapshot(after)) return [];

  const changes: string[] = [];

  if (before.name !== after.name) changes.push(de.changes.nameChange(before.name, after.name));
  if (before.colorIndex !== after.colorIndex) {
    changes.push(
      de.changes.colorChange(
        paletteColorName(before.colorIndex),
        paletteColorName(after.colorIndex),
      ),
    );
  }
  if (before.hasAccount !== after.hasAccount) {
    changes.push(after.hasAccount ? de.changes.accountAdded : de.changes.accountRemoved);
  }
  if (before.isAdmin !== after.isAdmin) {
    changes.push(after.isAdmin ? de.changes.adminGranted : de.changes.adminRevoked);
  }
  // Undefined on either side means the entry predates the role, not that it was
  // false — reading it as false would report a change that never happened.
  if (
    before.isCaregiver !== undefined &&
    after.isCaregiver !== undefined &&
    before.isCaregiver !== after.isCaregiver
  ) {
    changes.push(after.isCaregiver ? de.changes.viewOnlyRevoked : de.changes.viewOnlyGranted);
  }

  return changes;
}
